import { useState } from 'react';
import { Edit2, Download, Cloud, Calendar, Hash, DollarSign, Check, AlertTriangle, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import InvoiceContextMenu from './InvoiceContextMenu';

function InvoiceHistoryTable({ invoices, onEdit, onDownload, onExportToDrive, onDelete, onDownloadFormat, onMarkPaid, onMarkUnpaid }) {
  const [exportingId, setExportingId] = useState(null);
  const [exportedIds, setExportedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null); // { invoice, position: {x, y} }

  const handleExportToDrive = async (invoice) => {
    setExportingId(invoice.invoice_id);
    await onExportToDrive(invoice);
    setExportingId(null);
    setExportedIds(prev => new Set([...prev, invoice.invoice_id]));
  };

  const handleContextMenu = (e, invoice) => {
    e.preventDefault();
    setContextMenu({
      invoice,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextDownload = (invoice, format) => {
    if (onDownloadFormat) {
      onDownloadFormat(invoice, format);
    } else if (format === 'xlsx') {
      onDownload(invoice);
    }
  };

  // Check if extraction failed (no meaningful data)
  const isExtractionFailed = (invoice) => {
    if (invoice.extraction_failed) return true;
    const data = invoice.extracted_data;
    return !data.invoice_number && !data.vendor_name && !data.total_amount && !data.invoice_date;
  };

  if (!invoices || invoices.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Hash className="w-8 h-8 text-slate-400" />
        </div>
        <p className="text-slate-500">No invoices yet. Upload your first invoice to get started!</p>
      </div>
    );
  }

  // Count failed extractions
  const failedCount = invoices.filter(isExtractionFailed).length;

  return (
    <div className="space-y-4">
      {/* Warning banner for failed extractions */}
      {failedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3"
          data-testid="extraction-failed-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              {failedCount} invoice{failedCount > 1 ? 's' : ''} failed to extract data
            </p>
            <p className="text-xs text-amber-700 mt-1">
              These invoices show N/A for all fields. Please delete and re-upload with a clearer image, or manually enter the data.
            </p>
          </div>
        </motion.div>
      )}

      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="invoice-history-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] sm:min-w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="text-left px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">
                  Date
                </th>
                <th className="text-left px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="text-center px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-right px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="text-right px-3 sm:px-6 py-3 sm:py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((invoice, index) => {
                const failed = isExtractionFailed(invoice);
                return (
                  <motion.tr
                    key={invoice.invoice_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onContextMenu={(e) => handleContextMenu(e, invoice)}
                    className={`hover:bg-slate-50 transition-colors cursor-context-menu ${failed ? 'bg-amber-50/50' : ''}`}
                    data-testid={`invoice-row-${invoice.invoice_id}`}
                  >
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center space-x-2">
                        {failed ? (
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        ) : (
                          <Hash className="w-4 h-4 text-indigo-700 flex-shrink-0" />
                        )}
                        <span className={`text-xs sm:text-sm font-mono font-medium truncate ${failed ? 'text-amber-700' : 'text-slate-900'}`}>
                          {invoice.extracted_data.invoice_number || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className={`text-sm ${failed ? 'text-amber-600' : 'text-slate-600'}`}>
                          {invoice.extracted_data.invoice_date
                            ? format(new Date(invoice.extracted_data.invoice_date), 'MMM dd, yyyy')
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className={`text-xs sm:text-sm truncate block max-w-[100px] sm:max-w-none ${failed ? 'text-amber-700' : 'text-slate-900'}`}>
                        {invoice.extracted_data.vendor_name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <label className="inline-flex items-center cursor-pointer" title={invoice.status === 'paid' || invoice.payment_status === 'paid' ? 'Click to mark unpaid' : 'Click to mark paid'}>
                        <input
                          type="checkbox"
                          checked={invoice.status === 'paid' || invoice.payment_status === 'paid'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onMarkPaid && onMarkPaid(invoice.invoice_id);
                            } else {
                              onMarkUnpaid && onMarkUnpaid(invoice.invoice_id);
                            }
                          }}
                          className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                          data-testid={`paid-checkbox-${invoice.invoice_id}`}
                        />
                        <span className={`ml-2 text-xs font-medium ${
                          invoice.status === 'paid' || invoice.payment_status === 'paid'
                            ? 'text-emerald-600'
                            : 'text-slate-400'
                        }`}>
                          {invoice.status === 'paid' || invoice.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </span>
                      </label>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <DollarSign className={`w-4 h-4 ${failed ? 'text-amber-500' : 'text-emerald-500'}`} />
                        <span className={`text-xs sm:text-sm font-semibold font-mono ${failed ? 'text-amber-700' : 'text-slate-900'}`}>
                          {invoice.extracted_data.total_amount
                            ? invoice.extracted_data.total_amount.toFixed(2)
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                        {failed && onDelete && (
                          <button
                            onClick={() => onDelete(invoice)}
                            className="p-1.5 sm:p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
                            title="Delete and re-upload"
                            data-testid={`reupload-button-${invoice.invoice_id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(invoice)}
                          className="p-1.5 sm:p-2 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                          title={failed ? "Enter data manually" : "Edit"}
                          data-testid={`edit-button-${invoice.invoice_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDownload(invoice)}
                          className="p-1.5 sm:p-2 text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg transition-colors shadow-sm"
                          title="Download Excel"
                          data-testid={`download-button-${invoice.invoice_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExportToDrive(invoice)}
                          disabled={exportingId === invoice.invoice_id}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors hidden sm:flex items-center space-x-1 ${
                            exportedIds.has(invoice.invoice_id) || invoice.drive_file_id
                              ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                          } ${exportingId === invoice.invoice_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title="Export to Google Drive (requires setup)"
                          data-testid={`drive-button-${invoice.invoice_id}`}
                        >
                          {exportingId === invoice.invoice_id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-700"></div>
                          ) : exportedIds.has(invoice.invoice_id) || invoice.drive_file_id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Cloud className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <InvoiceContextMenu
          invoice={contextMenu.invoice}
          position={contextMenu.position}
          onClose={handleCloseContextMenu}
          onDelete={onDelete}
          onDownload={handleContextDownload}
        />
      )}
    </div>
  );
}

export default InvoiceHistoryTable;